<template>
  <div class="knw_widget_window">
    <ChatHeader
      v-if="showHeader"
      :show-avatar="true"
      :show-close-button="showWidgetHeaderCloseButton"
      :title="t('header.name')"
      :subtitle="t('header.subtitle')"
      :avatar-url="getDefaultAvatarUrl"
      :current-rating="currentRating"
      :show-rating="showRating"
      @on-new-rating-set="
        (newRating) => {
          setConversationRating(newRating);
        }
      "
      @close="closeWidgetClickHandler"
      @on-avatar-click="handleAvatarClick"
      @on-rating-button-click="handleRatingButtonClick"
    />
    <template v-if="showStartscreen">
      <ChatStartscreen />
    </template>
    <template v-else>
      <ChatConversation
        ref="bodywrapper"
        :greyed-out="isGrayedout"
        :show-powered-by="widgetStore.showPoweredBy"
        :conversation="bot.getConversation()"
        :show-ai-indicator="widgetStore.aiIndicator?.enabled || false"
      />
      <ChatInputs :max-height="height" :grayed-out="isGrayedout" />
    </template>
  </div>
</template>

<script setup lang="ts">
import ChatConversation from "@/components/widgetParts/chatPanel/chatConversation/ChatConversation.vue";
import ChatHeader from "@/components/widgetParts/chatPanel/chatHeader/ChatHeader.vue";
import ChatInputs from "@/components/widgetParts/chatPanel/chatInputs/ChatInputs.vue";
import ChatStartscreen from "@/components/widgetParts/chatPanel/ChatStartscreen.vue";

import { useWidgetStore } from "@/store/widget";
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useLayoutStore } from "@/store/layout";
import { useElementSize } from "@vueuse/core";
import { useMoinBot } from "@/services/bot/botv2";
import { useIframeManager } from "@/composables/useIframeManager";
import type { ElementLayout } from "@/types/ElementLayout";

import { useI18n } from "@/composables/useI18n";

const { t } = useI18n();
const bot = useMoinBot();

const props = defineProps<{
  frame: HTMLIFrameElement;
}>();

const widgetStore = useWidgetStore();
const layoutStore = useLayoutStore();

const bodywrapper = ref(null);

const {
  getDefaultAvatarUrl,
  getInputType,
  showRating,
  currentRating,
  addConversationInfo,
  setConversationRating,
  setShowConversationRating,
} = useMoinBot();
const { height } = useElementSize(bodywrapper);

const isGrayedout = computed(() => (getInputType.value.type === "fileupload" ? true : false));

const { showHeader, isWidgetOpen, isVisible, showWidgetHeaderCloseButton, showStartscreen } =
  storeToRefs(widgetStore);

const {
  widgetOffset: offsetOverwrites,
  isMobile,
  alignmentOrigin,
  zIndex,
} = storeToRefs(layoutStore);
const isElementVisible = computed(() => isWidgetOpen.value && isVisible.value);

const handleRatingButtonClick = () => {
  setShowConversationRating(true);
};

// ##############################
// Widget defaults:
// 0px on the x axis and 0px on the y axis mobile
// 20px on the x axis and 20px on the y axis on desktop
const xOffset = computed(() => {
  // defaults
  let value = isMobile.value ? "0px" : "20px";
  // overwrites via config / api if available
  if (offsetOverwrites.value?.x) {
    value = offsetOverwrites.value.x;
  }
  // on we omit position changes on the x axis and only allow changes on the y axis
  return isMobile.value ? "0px" : value;
});

const yOffset = computed(() => {
  // default behavior for mobile teaser and desktop teaser
  let value = isMobile.value ? "0px" : "20px";
  // if we have a value for the y axis set via api or config, we use that instead
  if (offsetOverwrites.value?.y) {
    value = offsetOverwrites.value.y;
  }
  // but we ignore sone axis on mobile
  return isMobile.value ? "0px" : value;
});

const widgetLayout = computed<ElementLayout>(() => {
  return {
    name: "widget",
    isElementVisible: isElementVisible.value,
    width: isMobile.value ? "100%!important" : "501px!important",
    height: isMobile.value ? "100dvh!important" : "740px!important",
    // on we omit all position changes on the widget on mobile
    x: xOffset.value,
    y: yOffset.value,
    xAlignment: alignmentOrigin.value.x,
    yAlignment: alignmentOrigin.value.y,
    zIndex: zIndex.value,
  };
});

const attrs = computed(() => {
  return {
    "aria-hidden": !isElementVisible.value,
    title: t("iframe.widget.title"),
    name: "moin-ai-widget",
  };
});
if (props.frame) {
  useIframeManager(props.frame, widgetLayout, { fullscreenable: true }, attrs);
}
// maxHeight: isMobile.value ? 'unset' : '100dvh!important',

// Click handler
const handleAvatarClick = (message: string) => addConversationInfo(message);
const closeWidgetClickHandler = () => widgetStore.closeWidget();
</script>

<style lang="scss">
.knw_widget_window {
  position: relative;
  pointer-events: all;
  max-width: 100%;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 2000;
  background-color: var(--moin-widget-background, $kw-global-background-messagecontainer);
  transition: bottom 0.5s ease-in-out, right 0.5s ease-in-out;

  @media (min-width: 480px) {
    border-top-left-radius: var(--kw-widget-global-borderradius-outer);
    border-top-right-radius: var(--kw-widget-global-borderradius-outer);
    border-bottom-right-radius: var(--kw-widget-global-borderradius-outer);
    border-bottom-left-radius: var(--kw-widget-global-borderradius-outer);

    /*
    TODO: Do we need to make the various shadow colors configurable like the old one?
    Shadow 1: Penumbra A (bottom)
    Shadow 2: Penumbra B (sides)
    Shadow 3: Umbra (bottom)
     */
    //box-shadow: 5px 5px 15px $kw-default-shadow-color-dark;
    box-shadow: 0 10px 15px -10px #0000001f, 0 0 10px -4px #00000020, 0 8px 10px -6px #0000001f;

    // for some reason, this fixes the height problem in safari (100% being too high)
    min-height: 100%;

    @supports (-ms-ime-align: auto) {
      border-radius: var(--kw-widget-global-borderradius-outer);
    }
  }
}
</style>
